import { Engine } from './core/Engine';
import { CelestialConfig } from './types';
import { COLORS } from './constants';
import { ControlsUI } from './ui/ControlsUI';

// Global reference for debugging
declare global {
  interface Window {
    engine: Engine;
    controlsUI: ControlsUI;
    IS_MOBILE_APP?: boolean;
  }
}

function createDemoSolarSystem(engine: Engine): void {
  // Create the Sun
  const sun: CelestialConfig = {
    id: 'sun',
    type: 'star',
    name: 'Sun',
    radius: 50,
    mass: 10000,
    position: { x: 0, y: 0, z: 0 },
    velocity: { x: 0, y: 0, z: 0 },
    color: COLORS.starYellow,
    emissive: COLORS.starYellow,
    emissiveIntensity: 2,
    isStatic: true,
  };
  engine.addCelestialBody(sun);

  // Create Earth-like planet
  const earth: CelestialConfig = {
    id: 'earth',
    type: 'planet',
    name: 'Earth',
    radius: 12,
    mass: 100,
    position: { x: 200, y: 0, z: 0 },
    velocity: { x: 0, y: 0, z: 0 },
    color: COLORS.earthBlue,
    atmosphere: {
      color: 0x88aaff,
      density: 0.5,
      height: 2,
    },
  };
  engine.createOrbitalBody(earth, 'sun');

  // Create Mars-like planet
  const mars: CelestialConfig = {
    id: 'mars',
    type: 'planet',
    name: 'Mars',
    radius: 8,
    mass: 60,
    position: { x: 320, y: 0, z: 0 },
    velocity: { x: 0, y: 0, z: 0 },
    color: COLORS.marsRed,
  };
  engine.createOrbitalBody(mars, 'sun');

  // Create Jupiter-like gas giant
  const jupiter: CelestialConfig = {
    id: 'jupiter',
    type: 'planet',
    name: 'Jupiter',
    radius: 30,
    mass: 500,
    position: { x: 500, y: 0, z: 50 },
    velocity: { x: 0, y: 0, z: 0 },
    color: COLORS.jupiterOrange,
  };
  engine.createOrbitalBody(jupiter, 'sun');

  // Create Saturn-like planet with rings
  const saturn: CelestialConfig = {
    id: 'saturn',
    type: 'planet',
    name: 'Saturn',
    radius: 25,
    mass: 400,
    position: { x: 700, y: 0, z: -30 },
    velocity: { x: 0, y: 0, z: 0 },
    color: COLORS.saturnGold,
    rings: {
      innerRadius: 35,
      outerRadius: 55,
      color: 0xccaa77,
      opacity: 0.6,
    },
  };
  engine.createOrbitalBody(saturn, 'sun');

  // Create a moon for Earth
  const moon: CelestialConfig = {
    id: 'moon',
    type: 'moon',
    name: 'Moon',
    radius: 4,
    mass: 10,
    position: { x: 230, y: 0, z: 0 },
    velocity: { x: 0, y: 0, z: 0 },
    color: 0xaaaaaa,
  };
  engine.createOrbitalBody(moon, 'earth');

  // Create some asteroids
  for (let i = 0; i < 20; i++) {
    const angle = (Math.PI * 2 * i) / 20 + Math.random() * 0.3;
    const distance = 400 + Math.random() * 50;

    const asteroid: CelestialConfig = {
      id: `asteroid-${i}`,
      type: 'asteroid',
      name: `Asteroid ${i + 1}`,
      radius: 1 + Math.random() * 3,
      mass: 1 + Math.random() * 5,
      position: {
        x: Math.cos(angle) * distance,
        y: (Math.random() - 0.5) * 20,
        z: Math.sin(angle) * distance,
      },
      velocity: { x: 0, y: 0, z: 0 },
      color: COLORS.asteroidGray,
    };
    engine.createOrbitalBody(asteroid, 'sun');
  }
}

function init(): void {
  console.log('Initializing Space Simulator...');

  const appContainer = document.getElementById('app')!;

  // Create engine
  const engine = new Engine({
    container: appContainer,
    antialias: true,
    pixelRatio: Math.min(window.devicePixelRatio, 2),
    camera: {
      orbitDistance: 600,
      orbitMinDistance: 50,
      orbitMaxDistance: 5000,
    },
  });

  // Expose for debugging
  window.engine = engine;

  // Create controls UI
  const controlsUI = new ControlsUI(appContainer);
  window.controlsUI = controlsUI;

  // Wire up touch controls if on mobile
  if (controlsUI.isMobileDevice()) {
    const cameraController = engine.getCameraController();
    const physicsWorld = engine.getPhysicsWorld();

    // Movement from left joystick
    controlsUI.onMove = (forward, strafe, _vertical) => {
      cameraController.setTouchMovement(forward, strafe);
    };

    // Fire button
    controlsUI.onFire = (pressed) => {
      engine.setTouchFiring(pressed);
    };

    // Time warp bar
    controlsUI.onTimeWarp = (scale) => {
      physicsWorld.setTimeScale(scale);
    };

    // Action buttons
    controlsUI.onAction = (action) => {
      switch (action) {
        case 'up':
          cameraController.setTouchVertical(1);
          break;
        case 'up_release':
          cameraController.setTouchVertical(0);
          break;
        case 'down':
          cameraController.setTouchVertical(-1);
          break;
        case 'down_release':
          cameraController.setTouchVertical(0);
          break;
        case 'pause':
          physicsWorld.setPaused(!physicsWorld.isPaused());
          break;
        case 'camera':
          const mode = cameraController.getMode();
          cameraController.setMode(mode === 'ship' ? 'orbit' : 'ship');
          break;
      }
    };
  }

  // Create demo solar system
  createDemoSolarSystem(engine);

  // Spawn some enemy targets
  engine.spawnRandomTargets(8);

  // Focus camera on the sun initially
  engine.getCameraController().setOrbitTarget(
    engine.getCelestialBody('sun')!.getPosition()
  );

  // Start the engine
  engine.start();

  console.log('Space Simulator started!');
  console.log('Controls:');
  console.log('  - WASD to fly ship');
  console.log('  - Mouse drag to look around');
  console.log('  - SPACE to fire weapons');
  console.log('  - C to toggle camera mode');
  console.log('  - P to pause, +/- for time scale');
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
